import { useRouter } from 'next/router'
import { Formik, Form, Field } from 'formik'
import Layout from 'components/layout'
import Pagination from 'components/pagination'
import { GetServerSideProps } from 'next'
import Link from 'next/link'
import api from 'services/api'
import routes from 'routes'
import BookListItem from 'models/book-list-item'
import { NextSeo } from 'next-seo'
import Breadcrumb from 'components/breadcrumb'
import { Heading } from 'pages'
import TagList from 'components/tag-list'
import AppDownload from 'components/app-download'
import Tag from 'models/tag'
import BookViewComplex from 'components/book-view-complex'
import { withStorage } from 'utils/withFunction'

const NUMBER_PER_PAGE = 50

const Search = ({
  keywords,
  books,
  page,
  count,
  hasNextPage,
  tags,
}: {
  keywords: string
  books: BookListItem[]
  page: number
  count: number
  hasNextPage: boolean
  tags: Tag[]
}): JSX.Element => {
  const router = useRouter()

  keywords = keywords.trim()

  async function onSubmit({ keywords }: { keywords: string }) {
    await router.push({
      pathname: '/search',
      query: { keywords: keywords.trim() },
    })
  }

  const previous =
    page > 1 ? routes.search.search({ keywords, page: page - 1 }) : undefined
  const next = hasNextPage
    ? routes.search.search({ keywords, page: page + 1 })
    : undefined

  return (
    <Layout showSearch={true}>
      <NextSeo title={`Similar to ${keywords} novel`} />
      <section className="desktop:flex">
        <section className="desktop:w-[1018px] mb-4">
          <div className="px-4 py-2 mb-2 lg:hidden">
            <Formik
              initialValues={{ keywords: keywords.trim() }}
              onSubmit={onSubmit}
            >
              {() => (
                <Form className="flex items-center">
                  <Field
                    type="text"
                    name="keywords"
                    placeholder="Search by title"
                    className="rounded-full border-0 bg-gray-100 flex-grow mr-3 px-4 py-2 focus:ring-0 placeholder-gray-400 caret-goldenrod"
                    autoFocus
                  />
                  <Link href="/" className="text-goldenrod cursor-pointer">
                    Cancel
                  </Link>
                </Form>
              )}
            </Formik>
          </div>
          <div className="hidden lg:block px-4 py-3">
            <Breadcrumb
              className="mr-4"
              items={[
                {
                  name: 'Search',
                  link: '/search',
                },
                {
                  name: `${keywords}`,
                },
              ]}
            />
          </div>
          {books.length > 0 && (
            <>
              <div className="px-4 mb-2 font-bold text-xl">
                {count} Results for{' '}
                <span className="text-goldenrod">{keywords}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[24px] px-4 py-[12px]">
                {books.map((item, index) => (
                  <BookViewComplex
                    book={item}
                    showIntro={true}
                    showTags={true}
                    key={index}
                  />
                ))}
              </div>
              <div className="m-4">
                <Pagination previous={previous} next={next} />
              </div>
            </>
          )}
          {keywords.trim() && !books.length && (
            <div>
              <NoResults />
            </div>
          )}
        </section>
        <section className="desktop:w-[422px]">
          <section className="px-4 mb-[40px]">
            <Heading title="Tags" moreLink={routes.tag.list()} />
            <TagList tags={tags} />
          </section>
          <section className="px-4 mb-[40px]">
            <AppDownload />
          </section>
        </section>
      </section>
    </Layout>
  )
}

const NoResults = () => {
  return (
    <div className="flex flex-col items-center py-20">
      <img
        src="/images/pic-no-results.png"
        width="278"
        height="278"
        className="mb-2"
        alt="no result"
      />
      <div className="text-gray-400 text-xs">Sorry! No results matched.</div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = withStorage(
  async ({ context }) => {
    const keywords = (context.query?.keywords ?? '') as string
    const page = +(context.query?.page ?? 1)
    const result = keywords
      ? await api.search.search({
          keyword: keywords,
          pageIndex: page,
          pageSize: NUMBER_PER_PAGE,
        })
      : []
    const books = result.data || []
    const tags = await api.tag.getHomeTags()
    // show recommendBooks when search result is null
    let recommendBooks = []
    if (!books.length) {
      recommendBooks = await api.book.getNodeBookList({
        nodeId: '64a53cf63c1bf6221a0e8a9b',
      })
    }
    const allBooks = [...recommendBooks].concat([...books])
    const total = result?.total || recommendBooks.length
    return {
      props: {
        keywords,
        books: allBooks,
        page,
        count: total,
        hasNextPage: total > NUMBER_PER_PAGE,
        tags,
      },
    }
  }
)

export default Search
